/* eslint-disable max-len */
/** @jsxImportSource @emotion/react */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Navigate, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { isValidString } from './utils/validation-types';
import SECRETS from './secrets';
import AppBar from './Components/AppBar';
import COLORS from './Components/styles/Colors';

const styles = {
  page: {
    position: 'fixed',
    height: '100%',
    width: '100%',
    top: 0,
    left: 0,
    overflow: 'hidden',
  },
  footer: {
    backgroundColor: 'rgb(64, 64, 64)',
    color: 'rgba(241, 245, 249, 0.9)',
    textAlign: 'right',
    padding: '5px',
    paddingRight: '25px',
    fontFamily: 'Roboto',
    fontSize: '0.875rem',
    minHeight: '38px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    fontWeight: 'bold',
    position: 'sticky',
    bottom: 0,
  },
  loaderContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '3rem',
  },
  tableWrapper: {
    width: '100%',
    height: 'calc(100vh - 64px)',
    overflow: 'auto',
    overflowX: 'auto',
    overflowY: 'auto',
  },
  emptyMessage: {
    padding: '1.5rem',
    textAlign: 'center',
    color: COLORS.SLATE400,
  },
};

const tableCSS = `
  .semanal-table {
    width: 100%;
    border-collapse: collapse;
    font-family: Roboto, sans-serif;
    font-size: 0.875rem;
  }

  .semanal-table thead th {
    background-color: rgb(64, 64, 64);
    color: ${COLORS.SLATE50};
    padding: 0.25rem 0.875rem;
    text-align: center;
    position: sticky;
    top: 0;
    z-index: 1;
  }

  .semanal-table thead th.sortable {
    white-space: nowrap;
    cursor: pointer;
    user-select: none;
  }

  .semanal-table thead th.sortable:hover {
    background-color: rgb(80, 80, 80);
  }

  .semanal-table tbody td {
    padding: 0.25rem 0.875rem;
    text-align: center;
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  }

  .semanal-table tbody tr:last-child td {
    border-bottom: none;
  }

  .semanal-table tbody tr:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }

  .loader-spinner {
    width: 4.25rem;
    --b: 8px;
    aspect-ratio: 1;
    border-radius: 50%;
    background: #514b82;
    -webkit-mask:
      repeating-conic-gradient(#0000 0deg,#000 1deg 70deg,#0000 71deg 90deg),
      radial-gradient(farthest-side,#0000 calc(100% - var(--b) - 1px),#000 calc(100% - var(--b)));
    -webkit-mask-composite: destination-in;
    mask-composite: intersect;
    animation: l5 1s infinite;
  }
  @keyframes l5 { to { transform: rotate(.5turn) } }
`;

const twoDigits = (num) => (num <= 9 ? `0${num}` : `${num}`);

const getToday = () => {
  const date = new Date();
  return `${date.getFullYear()}-${twoDigits(date.getMonth() + 1)}-${twoDigits(date.getDate())}`;
};

const getLastMonday = () => {
  const date = new Date();
  const day = date.getDay();
  const diff = (day === 0) ? 6 : day - 1;
  date.setDate(date.getDate() - diff);
  return `${date.getFullYear()}-${twoDigits(date.getMonth() + 1)}-${twoDigits(date.getDate())}`;
};

const removeTilde = (text) => (
  text ? text.normalize('NFD').replace(/[\u0300-\u036f]/g, '') : ''
);

const groupByOperator = (tripList) => tripList.reduce((acc, trip) => {
  const key = trip.idControlOperador;
  if (typeof acc[key] === 'undefined') {
    return { ...acc, [key]: { trips: [{ ...trip }] } };
  }
  return { ...acc, [key]: { trips: [...acc[key].trips, trip] } };
}, {});

const SemanalHistoryScreen = ({
  isLogged,
  token,
  logOut,
  secondary,
}) => {
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [tripsByOperator, setTripsByOperator] = useState({});
  const [routes, setRoutes] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [routeFilter, setRouteFilter] = useState([]);
  const [orderBy, setOrderBy] = useState('acc');
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setIsSearched] = useState(false);
  const navigate = useNavigate();

  const lastMonday = getLastMonday();
  const today = getToday();

  const parseDateOrDefault = (value, defaultValue) => {
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    return (isValidString(value) && dateRegex.test(value)) ? value : defaultValue;
  };

  const loadTableData = async (dateFromParam, dateToParam) => {
    try {
      const dateFromValue = parseDateOrDefault(dateFromParam, lastMonday);
      const dateToValue = parseDateOrDefault(dateToParam, today);
      setIsLoading(true);
      setTripsByOperator({});
      axios.defaults.headers.common.Authorization = `bearer ${token}`;
      axios.defaults.baseURL = SECRETS.SERVERURL;

      const [unitsResponse, tripsResponse, checkpointsResponse, routesResponse] = await Promise.all([
        axios.get('/unidades/organizacion?fields=idUnidad,unidad'),
        axios.post('/controlrecorrido', { fechaInicio: dateFromValue, fechaFin: dateToValue }),
        axios.get('/controlpunto/byOrganizacion'),
        axios.get('/controlruta'),
      ]);

      setRoutes(routesResponse.data);

      const unitsList = unitsResponse.data;
      const tripsList = tripsResponse.data;
      const checkpointsList = checkpointsResponse.data;

      const processedTrips = tripsList
        .filter((trip) => !trip.cancelado && trip.terminado)
        .map((trip) => {
          const routeCheckpoints = checkpointsList
            .filter((cp) => cp.idControlRuta === trip.idControlRuta)
            .sort((a, b) => a.tiempo - b.tiempo);

          const startDate = new Date(trip.inicio);

          const accumulatedDelay = trip.puntos
            .sort((a, b) => a.tiempo - b.tiempo)
            .reduce((acc, visitedPoint) => {
              const index = routeCheckpoints.findIndex((cp) => cp.id === visitedPoint.idControlPunto);
              if (index === routeCheckpoints.length - 1) return acc;
              const visitTime = new Date(visitedPoint.fecha);
              const checkpoint = checkpointsList.find((cp) => visitedPoint.idControlPunto === cp.id);
              if (typeof checkpoint === 'undefined') return acc;
              const diff = Math.floor(visitTime / 1000 / 60) - (Math.floor(startDate) / 1000 / 60) - checkpoint.tiempo;
              return diff <= 0 ? acc : acc + diff;
            }, 0);

          const unit = unitsList.find((u) => u.idUnidad === trip.idUnidad) ?? { unidad: '' };

          return {
            ...trip,
            acc: accumulatedDelay,
            unit,
          };
        });

      setTripsByOperator(groupByOperator(processedTrips));
      setIsLoading(false);
      setIsSearched(true);
    } catch (error) {
      console.error('Error al cargar datos de la tabla:', error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (secondary === 'false') {
      setDateFrom(lastMonday);
      setDateTo(today);
    } else {
      navigate('/');
    }
  }, []);

  const liftingSearch = (searchValueString) => setSearchValue(searchValueString);

  const reduceOperatorRow = (operatorKey) => {
    const result = tripsByOperator[operatorKey].trips.reduce((acc, trip) => {
      if (routeFilter.length > 0 && !routeFilter.includes(trip.idControlRuta)) return acc;
      const unitName = trip.unit?.unidad ?? '';
      const units = acc.units.includes(unitName) || unitName === ''
        ? acc.units
        : [...acc.units, unitName];
      return {
        ...acc,
        ...trip,
        count: acc.count + 1,
        acc: acc.acc + trip.acc,
        units,
      };
    }, { acc: 0, count: 0, units: [] });

    return {
      ...result,
      avg: result.count > 0 ? Math.round(result.acc / result.count) : 0,
      unitDisplay: result.units.length > 0 ? result.units.join(', ') : '',
    };
  };

  const handleDateFrom = (newValue) => {
    const newDateString = newValue ? newValue.format('YYYY-MM-DD') : lastMonday;
    const formatDateFrom = parseDateOrDefault(newDateString, lastMonday);
    let formatDateTo = parseDateOrDefault(dateTo, today);
    if (dayjs(formatDateTo).isBefore(dayjs(formatDateFrom))) formatDateTo = formatDateFrom;
    setDateFrom(formatDateFrom);
    setDateTo(formatDateTo);
  };

  const handleDateTo = (newValue) => {
    const newDateString = newValue ? newValue.format('YYYY-MM-DD') : today;
    const formatDateTo = parseDateOrDefault(newDateString, today);
    setDateTo(formatDateTo);
  };

  const buildOperatorRows = () => Object.keys(tripsByOperator)
    .map((operatorKey) => reduceOperatorRow(operatorKey))
    .filter((row) => row.count > 0);

  const filteredRows = buildOperatorRows()
    .filter((row) => {
      if (!searchValue) return true;
      const search = removeTilde(searchValue.toLowerCase());
      const name = removeTilde(row.nombreOperador?.toLowerCase() || '');
      const unit = removeTilde(row.unitDisplay?.toLowerCase() || '');
      return name.includes(search) || unit.includes(search);
    })
    .sort((a, b) => b[orderBy] - a[orderBy]);

  if (isLogged !== 'true') return <Navigate to="/" replace />;

  return (
    <div style={styles.page}>
      <style>{tableCSS}</style>
      <AppBar
        visibleStateRows={[]}
        dateFilter={undefined}
        handleDateFilter={undefined}
        dateFrom={dateFrom}
        dateTo={dateTo}
        handleDateFrom={handleDateFrom}
        handleDateTo={handleDateTo}
        filterRoutes={routeFilter}
        rutas={routes}
        newTracksList={undefined}
        searchBarTextDisplay={searchValue}
        secondary={secondary}
        handleSearch={liftingSearch}
        handleLogOut={logOut}
        handleStateRow={undefined}
        handleFilterRoutes={(filter) => setRouteFilter(filter)}
        handleUpdateTracks={undefined}
        recoversCountMessage={undefined}
        isFilterStringToday={undefined}
        location="Atrasos Semanal"
        handleCopy={undefined}
        withSearchButton
        onSearch={() => loadTableData(dateFrom, dateTo)}
      />
      {isLoading ? (
        <div style={styles.loaderContainer}>
          <div className="loader-spinner" />
        </div>
      ) : (
        <div style={styles.tableWrapper}>
          <div>
            <table className="semanal-table">
              <thead>
                <tr>
                  <th>Operador</th>
                  <th>Unidad</th>
                  <th className="sortable" onClick={() => setOrderBy('acc')}>
                    {`Acumulado ${orderBy === 'acc' ? '↓' : ''}`}
                  </th>
                  <th className="sortable" onClick={() => setOrderBy('count')}>
                    {`# Recorridos ${orderBy === 'count' ? '↓' : ''}`}
                  </th>
                  <th className="sortable" onClick={() => setOrderBy('avg')}>
                    {`Promedio por vuelta ${orderBy === 'avg' ? '↓' : ''}`}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row) => (
                  <tr key={`op-${row.idControlOperador}-${row.acc}-${row.count}`}>
                    <td>{row.nombreOperador}</td>
                    <td>{row.unitDisplay}</td>
                    <td>{row.acc}</td>
                    <td>{row.count}</td>
                    <td>{row.avg}</td>
                  </tr>
                ))}
                {filteredRows.length === 0 && (
                  <tr>
                    <td colSpan={5} style={styles.emptyMessage}>
                      {hasSearched ? 'No se encontraron registros.' : 'Por favor introduzca las fechas a buscar y presione Mostrar.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div style={styles.footer}>
            {`Total Operadores mostrados: ${filteredRows.length}`}
          </div>
        </div>
      )}
    </div>
  );
};

export default SemanalHistoryScreen;
